import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import { useHorizontalScroll, useMouseDown } from 'hooks';
import FlexContainer from '../FlexContainer';
import View from '../View';
import styles from './ScrollControl.module.scss';

const pixelGap = 10;

/**
 * ScrollControl
 *
 * @return {JSX.Element}
 */
const ScrollControl = ({ className, children, controls, horizontal }) => {
  const refControlBack = useRef(null);
  const refControlForward = useRef(null);
  const refContainer = useRef(null);
  const refMain = useRef(null);
  const refIntervalRef = useRef(null);
  const refDateMovementStart = useRef(null);

  const isStepControls = typeof controls === 'number';

  const scrollTo = (stepsDirection, behavior = 'smooth') => {
    refContainer.current.scrollTo({
      top: isStepControls
        ? refContainer.current.scrollTop + controls * stepsDirection
        : stepsDirection > 0
        ? refContainer.current.scrollHeight
        : 0,
      left: isStepControls
        ? refContainer.current.scrollLeft + controls * stepsDirection
        : stepsDirection > 0
        ? refContainer.current.scrollWidth
        : 0,
      behavior,
    });
  };

  const scrollBack = () => scrollTo(-1);
  const scrollForward = () => scrollTo(1);

  const movementLoop = (direction) => {
    const timeMultiplier =
      (new Date() - refDateMovementStart.current) / 1000 + 1;
    scrollTo(direction * 0.01 * timeMultiplier, 'auto');
    refIntervalRef.current = setTimeout(() => movementLoop(direction), 10);
  };

  const startMovement = (direction) => {
    refDateMovementStart.current = new Date();
    movementLoop(direction);
  };

  const stopMovement = () => {
    clearTimeout(refIntervalRef.current);
    refIntervalRef.current = null;
  };

  const startMovementForward = () => startMovement(1);
  const startMovementBack = () => startMovement(-1);

  useMouseDown(
    refControlForward,
    startMovementForward,
    stopMovement,
    scrollForward
  );

  useMouseDown(refControlBack, startMovementBack, stopMovement, scrollBack);

  useHorizontalScroll(refContainer, horizontal);

  useEffect(() => {
    const containerEl = refContainer.current;
    const scrollPositionProperty = horizontal ? 'scrollLeft' : 'scrollTop';
    const scrollSizeProperty = horizontal ? 'scrollWidth' : 'scrollHeight';
    const offsetSizeProperty = horizontal ? 'offsetWidth' : 'offsetHeight';

    const scrollListener = ({ target }) => {
      const scrollPosition = target[scrollPositionProperty];
      const scrollSize = target[scrollSizeProperty];
      const offsetSize = target[offsetSizeProperty];
      const isAtStart = scrollPosition - pixelGap < 0;
      const isAtEnd = scrollPosition + offsetSize + pixelGap > scrollSize;

      if (isAtStart) {
        refControlBack.current.classList.remove(styles.Control__Visible);
      } else {
        refControlBack.current.classList.add(styles.Control__Visible);
      }

      if (isAtEnd) {
        refControlForward.current.classList.remove(styles.Control__Visible);
      } else {
        refControlForward.current.classList.add(styles.Control__Visible);
      }
    };

    scrollListener({ target: containerEl });

    containerEl.addEventListener('scroll', scrollListener);

    return () => containerEl.removeEventListener('scroll', scrollListener);
  }, [horizontal]);

  return (
    <FlexContainer
      flex
      className={classNames(
        styles.ScrollControl,
        horizontal && styles.ScrollControl__horizontal,
        className,
        ScrollControl.displayName
      )}
      ref={refMain}
    >
      <FlexContainer overflow className={styles.Container} ref={refContainer}>
        <FlexContainer gap column={!horizontal}>
          {children}
        </FlexContainer>
      </FlexContainer>
      {controls ? (
        <>
          <View
            ref={refControlBack}
            className={classNames(styles.Control, styles.Control__Back)}
            // onClick={scrollBack}
            // onMouseDown={handleMouseDownBack}
            // onMouseUp={handleMouseUpBack}
          ></View>
          <View
            ref={refControlForward}
            className={classNames(styles.Control, styles.Control__Forward)}
            // onClick={scrollForward}
            // onMouseDown={handleMouseDownForward}
            // onMouseUp={handleMouseUpForward}
          ></View>
        </>
      ) : null}
    </FlexContainer>
  );
};

ScrollControl.displayName = 'ScrollControl';

export default ScrollControl;
